import { BackdropLoading } from '@/components/backdrop-loading'
import ConfirmModal from '@/components/confirm-modal'
import { ModalActions, ModalContainer } from '@/components/container/container'
import { FormDialog } from '@/components/form-dialog'
import SalesTaskFinishModal, { type SalesTaskFinishAction } from '@/components/sales/SalesTaskFinishModal'
import {
    deleteAgendaEvent,
    deleteRecurringEventConfig,
    getRecurringEventConfigs,
    RecurringEventConfig,
    RecurringFrequency,
    updateAgendaEvent,
} from '@/lib/api/agenda/agenda-api'
import { AgendaEvent } from '@/lib/api/common/interfaces/agenda.interface'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { format, formatISO } from 'date-fns'
import { toDate } from 'date-fns-tz'
import { ptBR } from 'date-fns/locale'
import React, { useEffect, useState } from 'react'
import { CiCalendar } from 'react-icons/ci'
import { FaExternalLinkAlt, FaWhatsapp } from 'react-icons/fa'
import { FaBuilding, FaTrash, FaUserDoctor } from 'react-icons/fa6'
import {
    MdCheck,
    MdClose,
    MdEmail,
    MdLocalHospital,
    MdMeetingRoom,
    MdPersonSearch,
    MdRepeat,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getEventTypeLabels, isTaskType } from './utils/status-colors'

interface Props {
    event: AgendaEvent
    onClose: (reload?: boolean) => void
}

const timeZone = 'America/Sao_Paulo'

const FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
    DAILY: 'Diario',
    WEEKLY: 'Semanal',
    BIWEEKLY: 'Quinzenal',
    MONTHLY: 'Mensal',
    QUARTERLY: 'Trimestral',
    SEMIANNUAL: 'Semestral',
    ANNUAL: 'Anual',
}

export function EventDialog({ event, onClose }: Props) {
    const [showChangeDateModal, setShowChangeDateModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showDeleteRecurrenceModal, setShowDeleteRecurrenceModal] =
        useState(false)
    const [loading, setLoading] = useState(false)
    const [recurringConfig, setRecurringConfig] =
        useState<RecurringEventConfig | null>(null)
    const navigate = useNavigate()
    const { clientLabel } = useClientLabel()
    const eventTypeLabels = getEventTypeLabels(useServiceLabel())
    const { canUpdate: canUpdateLeads } = useResourcePermissions('sales', 'leads')
    const [finishingTask, setFinishingTask] = useState<SalesTaskFinishAction | null>(null)

    // Tarefa de vendas (docs/sales-tasks.md): tem prazo, não duração, e se encerra
    // concluindo ou cancelando pelo mesmo caminho da tela do lead.
    const isTask = isTaskType(event.type)
    const isOpenTask = isTask && ['PENDING', 'CONFIRMED', 'OVERDUE'].includes(event.status)

    // Evento vinculado a um atendimento é a projeção dele na agenda — e um mesmo
    // atendimento pode ter mais de um evento (agenda do profissional e a do parceiro).
    // Excluir a projeção deixaria o atendimento fora da agenda com a cobrança ainda viva,
    // então o backend recusa; aqui o botão Excluir dá lugar a um atalho para o atendimento,
    // de onde a exclusão cancela a cobrança e revoga o repasse ao parceiro.
    const linkedAppointment = event.examId
        ? {
              label: 'exame',
              article: 'a um',
              path: `/bookings/exams/${event.examId}`,
              editPath: `/atendimento/scheduling/${event.examId}?type=EXAM`,
          }
        : event.consultationId
          ? {
                label: 'consulta',
                article: 'a uma',
                path: `/bookings/consultations/${event.consultationId}`,
                editPath: `/atendimento/scheduling/${event.consultationId}?type=CONSULTATION`,
            }
          : event.orderId
            ? {
                  label: 'procedimento',
                  article: 'a um',
                  path: `/bookings/orders/${event.orderId}`,
                  editPath: `/atendimento/scheduling/${event.orderId}?type=PROCEDURE`,
              }
            : null

    const professionalId = event.agenda?.professional?.id
    const partnerId = event.partner?.id ?? event.partnerId

    useEffect(() => {
        loadRecurringConfig()
    }, [event.agendaId])

    const loadRecurringConfig = async () => {
        try {
            const response = await getRecurringEventConfigs(event.agendaId)
            if (response.success && response.result) {
                const config = response.result.find(
                    c => c.title === event.title && c.active
                )
                setRecurringConfig(config || null)
            }
        } catch {
            // silencioso
        }
    }

    const handleDeleteRecurrence = async () => {
        if (!recurringConfig) return
        setLoading(true)
        try {
            const response = await deleteRecurringEventConfig(
                recurringConfig.id
            )
            if (response.success) {
                toast.success('Recorrencia removida com sucesso')
                setShowDeleteRecurrenceModal(false)
                setRecurringConfig(null)
            } else {
                toast.error(response.error || 'Erro ao remover recorrência')
            }
        } catch {
            toast.error('Erro ao remover recorrência')
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteSchedule = async () => {
        setLoading(true)
        try {
            const response = await deleteAgendaEvent(event.id)
            if (response.success) {
                toast.success('Evento excluído com sucesso')
                setShowDeleteModal(false)
                onClose(true)
            } else {
                toast.error(response.error || 'Erro ao excluir evento')
            }
        } catch (error) {
            toast.error('Erro ao excluir evento')
        } finally {
            setLoading(false)
        }
    }

    const handleUpdateDate = async (values: any) => {
        const date = values.date
        const hour = values.hour
        const end_date = values.end_date

        if (!end_date) {
            return toast.error('Defina a previsão de término')
        }

        if (!date) {
            return toast.error('Defina a data')
        }

        if (!hour) {
            return toast.error('Defina a hora')
        }

        const [hours, minutes] = hour.split(':').map(Number)
        const [hours2, minutes2] = end_date.split(':').map(Number)

        if (hours < 0 || hours > 23) {
            return toast.error('Horas inválidas!')
        }

        if (minutes < 0 || minutes > 59) {
            return toast.error('Minutos inválidos!')
        }

        if (hours2 < 0 || hours2 > 23) {
            return toast.error('Horas inválidas!')
        }

        if (minutes2 < 0 || minutes2 > 59) {
            return toast.error('Minutos inválidos!')
        }

        const split = date.split('/')

        const strDate = `${split[2]}-${split[1]}-${split[0]} ${hour}:00`
        const strEndDate = `${split[2]}-${split[1]}-${split[0]} ${end_date}:00`

        const parsedDate = toDate(strDate, {
            timeZone,
        })

        const parsedEndDate = toDate(strEndDate, {
            timeZone,
        })

        const hourEndDate = parsedEndDate.getHours()
        const hourDate = parsedDate.getHours()
        const minuteEndDate = parsedEndDate.getMinutes()
        const minuteDate = parsedDate.getMinutes()

        if (
            (hourEndDate === 0 ? 24 : hourEndDate) <
                (hourDate === 0 ? 24 : hourDate) ||
            (hourEndDate === hourDate && minuteEndDate <= minuteDate)
        ) {
            return toast.error(
                'A hora da previsão de término precisa ser maior que o início!'
            )
        }

        const isoDate = formatISO(parsedDate)
        const isoEndDate = formatISO(parsedEndDate)

        setLoading(true)
        try {
            const response = await updateAgendaEvent(event.id, {
                startDate: isoDate,
                endDate: isoEndDate,
            })

            if (response.success) {
                toast.success('Evento reagendado com sucesso')
                setShowChangeDateModal(false)
                onClose(true)
            } else {
                toast.error(response.error || 'Erro ao reagendar evento')
            }
        } catch (error) {
            toast.error('Erro ao reagendar evento')
        } finally {
            setLoading(false)
        }
    }

    const startDate = new Date(event.startDate)
    const endDate = new Date(event.endDate)

    /**
     * Por ordem de chegada não se imprime horário.
     *
     * O evento guarda 00:00 em `startDate` porque a coluna é obrigatória, e a tela
     * anunciava "13/08/2026 às 00:00 - 00:30h" — uma hora que ninguém marcou, para
     * um atendimento que existe justamente por não ter hora. Quem responde é
     * `allDay`, gravado pelo agendamento junto com o `arrivalOrder` da entidade.
     */
    const arrivalOrder = !!event.allDay
    const formattedDate = format(
        startDate,
        arrivalOrder ? 'dd/MM/yyyy' : "dd/MM/yyyy 'às' HH:mm",
        { locale: ptBR }
    )
    const formattedEndTime = format(endDate, 'HH:mm', { locale: ptBR })

    // Duração em minutos, formatada (ex.: "1h 30min", "45min").
    const durationMinutes = Math.max(
        0,
        Math.round((endDate.getTime() - startDate.getTime()) / 60000)
    )
    const durationLabel =
        durationMinutes >= 60
            ? `${Math.floor(durationMinutes / 60)}h${durationMinutes % 60 ? ` ${durationMinutes % 60}min` : ''}`
            : `${durationMinutes}min`

    // Hospital do atendimento vem da entidade ligada (exame/consulta/procedimento).
    const hospital =
        event.exam?.hospital ||
        event.consultation?.hospital ||
        event.order?.hospital ||
        null

    return (
        <React.Fragment>
            <dialog className="modal modal-open">
                <ModalContainer size="sm">
                    <div className="flex flex-col gap-2 event-info">
                        {/*
                          O cabeçalho é o paciente. O título com o número
                          ("Consulta #3723") identifica o registro, não a pessoa,
                          e fica abaixo como referência.
                        */}
                        <div className="flex flex-col mb-3">
                            <span className="!text-lg font-bold text-black">
                                {event.client?.name || event.title}
                            </span>
                            {event.client && (
                                <span className="text-sm text-base-content/60">
                                    {event.title}
                                </span>
                            )}
                        </div>

                        {event.client &&
                            (event.client.whatsAppNumber ||
                                event.client.cpf) && (
                                <div className="event-item">
                                    <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-base-content/70">
                                        {event.client.whatsAppNumber && (
                                            <span className="flex items-center gap-1">
                                                <FaWhatsapp className="text-base-content/40" />
                                                {event.client.whatsAppNumber}
                                            </span>
                                        )}
                                        {event.client.cpf && (
                                            <span>CPF: {event.client.cpf}</span>
                                        )}
                                    </span>
                                </div>
                            )}

                        {event.type && (
                            <div className="event-item">
                                <span className="event-name">Tipo</span>
                                <span className="event-value">
                                    {eventTypeLabels[event.type] ||
                                        event.type}
                                </span>
                            </div>
                        )}

                        {event.description && (
                            <div className="event-item">
                                <span className="event-name">Descrição</span>
                                <span className="event-value whitespace-pre-wrap">
                                    {event.description}
                                </span>
                            </div>
                        )}

                        {event.agenda?.professional && (
                            <div className="event-item">
                                <span className="event-name">Profissional</span>
                                <span className="event-value font-bold flex items-center gap-2">
                                    <FaUserDoctor />{' '}
                                    {event.agenda.professional.name}
                                </span>
                            </div>
                        )}

                        {event.partner && (
                            <div className="event-item">
                                <span className="event-name">Parceiro</span>
                                <span className="event-value font-bold flex items-center gap-2">
                                    <FaBuilding />{' '}
                                    {event.partner.name ||
                                        event.partner.tradingName}
                                </span>
                            </div>
                        )}

                        {hospital && (
                            <div className="event-item">
                                <span className="event-name">Hospital</span>
                                <span className="event-value font-bold flex items-center gap-2">
                                    <MdLocalHospital />{' '}
                                    {hospital.name || hospital.tradingName}
                                </span>
                            </div>
                        )}

                        {event.room && (
                            <div className="event-item">
                                <span className="event-name">Sala</span>
                                <span className="event-value flex items-center gap-2">
                                    <MdMeetingRoom /> {event.room.name}
                                </span>
                            </div>
                        )}

                        <div className="event-item">
                            <span className="event-name">
                                {arrivalOrder ? 'Data' : isTask ? 'Prazo' : 'Início e Término'}
                            </span>
                            <span className="event-value">
                                {arrivalOrder || isTask
                                    ? formattedDate
                                    : `${formattedDate} - ${formattedEndTime}h`}
                            </span>
                        </div>

                        {/* Sem hora marcada não há duração a anunciar: os 30 min são
                            o padrão que a coluna exige, não um compromisso com o
                            paciente. */}
                        {!arrivalOrder && !isTask && (
                            <div className="event-item">
                                <span className="event-name">Duração</span>
                                <span className="event-value">
                                    {durationLabel}
                                </span>
                            </div>
                        )}

                        {arrivalOrder && (
                            <div className="event-item">
                                <span className="event-name">Horário</span>
                                <span className="event-value">
                                    Por ordem de chegada
                                </span>
                            </div>
                        )}

                        <div className="event-item">
                            <span className="event-name">Status</span>
                            <span className="event-value">
                                {event.status === 'PENDING' && 'Pendente'}
                                {event.status === 'CONFIRMED' && 'Confirmado'}
                                {event.status === 'CANCELED' && 'Cancelado'}
                                {event.status === 'COMPLETED' && 'Concluído'}
                                {event.status === 'NO_SHOW' && 'Não compareceu'}
                                {event.status === 'OVERDUE' && 'Atrasado'}
                                {event.status === 'NO_SHOW_JUSTIFIED' &&
                                    'Falta justificada'}
                                {event.status === 'RESCHEDULED' && 'Reagendado'}
                            </span>
                        </div>

                        {event.notes && (
                            <div className="event-item">
                                <span className="event-name">Observações</span>
                                <span className="event-value whitespace-pre-wrap">
                                    {event.notes}
                                </span>
                            </div>
                        )}

                        {(event.notificationEmail ||
                            event.whatsappNotificationNumber) && (
                            <div className="mt-3 pt-3 border-t border-base-300/50">
                                <span className="event-name text-xs text-base-content/50 uppercase tracking-wide">
                                    Notificações
                                </span>
                                {event.notificationEmail && (
                                    <div className="flex items-center gap-2 mt-1 text-sm text-base-content/70">
                                        <MdEmail className="text-base-content/40" />
                                        {event.notificationEmail}
                                    </div>
                                )}
                                {event.whatsappNotificationNumber && (
                                    <div className="flex items-center gap-2 mt-1 text-sm text-base-content/70">
                                        <FaWhatsapp className="text-base-content/40" />
                                        {event.whatsappNotificationNumber}
                                    </div>
                                )}
                            </div>
                        )}

                        {recurringConfig && (
                            <div className="mt-3 pt-3 border-t border-base-300/50">
                                <span className="event-name text-xs text-base-content/50 uppercase tracking-wide">
                                    Recorrência
                                </span>
                                <div className="flex items-center gap-2 mt-1 text-sm text-base-content/70">
                                    <MdRepeat className="text-base-content/40" />
                                    {
                                        FREQUENCY_LABELS[
                                            recurringConfig.frequency
                                        ]
                                    }
                                </div>
                                <div className="text-xs text-base-content/50 mt-1">
                                    Proxima ocorrência:{' '}
                                    {format(
                                        new Date(
                                            recurringConfig.nextOccurrence
                                        ),
                                        'dd/MM/yyyy',
                                        { locale: ptBR }
                                    )}
                                </div>
                                <button
                                    className="btn btn-xs btn-outline btn-error mt-2"
                                    onClick={() =>
                                        setShowDeleteRecurrenceModal(true)
                                    }
                                >
                                    Remover recorrência
                                </button>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 mt-5">
                            {event.clientId && (
                                <button
                                    className="btn btn-sm"
                                    onClick={() =>
                                        navigate(
                                            `/person/clients/${event.clientId}`
                                        )
                                    }
                                >
                                    Ver {clientLabel}
                                </button>
                            )}

                            {linkedAppointment && (
                                <button
                                    className="btn btn-sm btn-ghost gap-1"
                                    onClick={() =>
                                        navigate(linkedAppointment.path)
                                    }
                                >
                                    <FaExternalLinkAlt /> Ver agendamento
                                </button>
                            )}

                            {isTask && event.leadId && (
                                <button
                                    className="btn btn-sm btn-ghost gap-1"
                                    onClick={() => navigate(`/sales/leads/${event.leadId}`)}
                                >
                                    <MdPersonSearch /> Abrir lead
                                </button>
                            )}

                            {isOpenTask && canUpdateLeads && (
                                <>
                                    <button className="btn btn-sm btn-success gap-1" onClick={() => setFinishingTask('complete')}>
                                        <MdCheck /> Concluir
                                    </button>
                                    <button className="btn btn-sm btn-ghost gap-1 text-error" onClick={() => setFinishingTask('cancel')}>
                                        <MdClose /> Cancelar tarefa
                                    </button>
                                </>
                            )}

                            {professionalId && (
                                <button
                                    className="btn btn-sm btn-ghost gap-1"
                                    onClick={() =>
                                        navigate(
                                            `/person/professionals/${professionalId}`
                                        )
                                    }
                                >
                                    <FaUserDoctor /> Profissional
                                </button>
                            )}

                            {partnerId && (
                                <button
                                    className="btn btn-sm btn-ghost gap-1"
                                    onClick={() =>
                                        navigate(
                                            `/person/partners/${partnerId}`
                                        )
                                    }
                                >
                                    <FaBuilding /> Parceiro
                                </button>
                            )}

                            {linkedAppointment ? (
                                // Reagendar/editar um atendimento é feito no próprio form
                                // de agendamento (onde valor, cobrança e local são ajustados).
                                // A exclusão fica na tela de detalhe do atendimento.
                                <button
                                    className="btn btn-sm btn-base-100 gap-1"
                                    onClick={() =>
                                        navigate(linkedAppointment.editPath)
                                    }
                                >
                                    <CiCalendar /> Reagendar
                                </button>
                            ) : (
                                // Evento avulso (sem atendimento vinculado): reagenda inline
                                // e ainda pode ser excluído aqui (não há tela de detalhe).
                                <>
                                    <button
                                        className="btn btn-sm btn-base-100 gap-1"
                                        onClick={() =>
                                            setShowChangeDateModal(true)
                                        }
                                    >
                                        <CiCalendar /> Reagendar
                                    </button>
                                    {/* Tarefa se encerra cancelando, que fica no histórico do lead. */}
                                    {!isTask && (
                                        <button
                                            className="btn btn-sm btn-outline btn-error gap-1"
                                            onClick={() => setShowDeleteModal(true)}
                                        >
                                            <FaTrash /> Excluir
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <ModalActions>
                        <button
                            className="btn btn-ghost"
                            onClick={() => onClose()}
                        >
                            Fechar
                        </button>
                    </ModalActions>
                </ModalContainer>
            </dialog>

            {showChangeDateModal && (
                <FormDialog
                    onCancel={() => setShowChangeDateModal(false)}
                    title="Reagendamento"
                    initialState={{
                        date: format(startDate, 'dd/MM/yyyy', {
                            locale: ptBR,
                        }),
                        hour: format(startDate, 'HH:mm', {
                            locale: ptBR,
                        }),
                        end_date: format(endDate, 'HH:mm', {
                            locale: ptBR,
                        }),
                    }}
                    fields={[
                        {
                            name: 'date',
                            placeholder: '00/00/0000',
                            type: 'text',
                            required: true,
                            mask: '99/99/9999',
                        },
                        {
                            name: 'hour',
                            placeholder: 'Horas (00:00)',
                            type: 'text',
                            required: true,
                            mask: '99:99',
                        },
                        {
                            name: 'end_date',
                            placeholder: 'Previsão de término (00:00)',
                            type: 'text',
                            required: true,
                            mask: '99:99',
                        },
                    ]}
                    onSubmit={handleUpdateDate}
                />
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteSchedule}
                title="Excluir evento"
                message="Tem certeza que deseja excluir este evento?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={loading}
            />

            <ConfirmModal
                isOpen={showDeleteRecurrenceModal}
                onClose={() => setShowDeleteRecurrenceModal(false)}
                onConfirm={handleDeleteRecurrence}
                title="Remover recorrência"
                message="Tem certeza que deseja remover a recorrência deste evento? Os eventos já criados não serão afetados, mas novos eventos não serão mais criados automaticamente."
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
                loading={loading}
            />

            {finishingTask && (
                <SalesTaskFinishModal
                    task={{ id: event.id, title: event.title }}
                    action={finishingTask}
                    onClose={finished => {
                        setFinishingTask(null)
                        if (finished) onClose(true)
                    }}
                />
            )}

            <BackdropLoading visible={loading} />
        </React.Fragment>
    )
}
